import React from 'react';
import { useIntl } from '../../util/reactIntl';

// Utils
import { SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_TEXT, SCHEMA_TYPE_SHORT_TEXT, SCHEMA_TYPE_YOUTUBE } from '../../util/types';
import {
  isFieldForCategory,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';

import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';

/**
 * Renders custom listing fields.
 * - SectionDetailsMaybe is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnumMaybe is used if schemaType is 'multi-enum'
 * - SectionTextMaybe is used if schemaType is 'text'
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration } = props;
  const intl = useIntl();
  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };
  const propsForCustomFields =
    pickCustomFieldProps(
      publicData,
      metadata,
      listingFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  // Filter out artist and albumtitle fields as they are handled in ListingPageCarousel
  const filteredPropsForCustomFields = propsForCustomFields.filter(
    field => field.key !== 'artist' && field.key !== 'albumtitle'
  );

  return (
    <>
      <SectionDetailsMaybe {...props} isFieldForCategory={isFieldForSelectedCategories} propsForCustomFields={propsForCustomFields}/>
      {filteredPropsForCustomFields.map(customFieldProps => {
        const { schemaType, key, ...fieldProps } = customFieldProps;

        const localizedHeading = intl.formatMessage(
          { id: `ListingPage.field.${key}.label`, defaultMessage: fieldProps.heading || key }
        );

        const localizedProps =
          schemaType === SCHEMA_TYPE_MULTI_ENUM
            ? {
                ...fieldProps,
                heading: localizedHeading,
                options: (fieldProps.options || []).map(opt => ({
                  ...opt,
                  label: intl.formatMessage({
                    id: `ListingPage.field.${key}.option.${opt.key}`,
                    defaultMessage: opt.label || opt.key,
                  }),
                })),
              }
            : { ...fieldProps, heading: localizedHeading };

        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe key={key} {...localizedProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe key={key} {...localizedProps} />
        ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
          <SectionYoutubeVideoMaybe key={key} {...localizedProps} />
        ) : null;
      })}
    </>
  );
};

export default CustomListingFields;
